import { useState, useEffect, useMemo, useCallback } from 'react'
import { Modal, Input, DatePicker, Icon, Text, Divider } from '@lmc/ui-kit'
import { Col, Form, Radio, Row } from 'antd'
import { getNominative } from '../../../common/utils'
import { FormWarning } from '../../forms/FormWarning'
import styled from 'styled-components'
import {
  PeopleNominatives,
  ResidentCreateBody,
  ResidentStatus
} from '@lmc/ts-common'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { addResidentModalConstants as c } from '../../../locale/addResidentModal'

dayjs.extend(customParseFormat)

const DATE_FORMAT = 'DD-MM-YYYY'

const DUMMY_DATA = {
  male: {
    firstName: 'John',
    lastName: 'Q. Public',
    gender: 'male',
    dateOfBirth: '09-08-1982',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  },
  female: {
    firstName: 'Jane',
    lastName: 'Smith',
    gender: 'female',
    dateOfBirth: '24-12-1962',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  }
}

export interface PartialResident {
  name: {
    first: string
    last: string
  }
  status: ResidentStatus
  gender?: 'male' | 'female'
}

type FormValues = {
  status: ResidentStatus
  name: {
    first: string
    last: string
  }
  preferredName?: string
  gender: 'male' | 'female'
  dateOfBirth: dayjs.Dayjs
  dols?: boolean
  dnacpr?: boolean
  fundingSource?: string
}

type AddResidentModal2Props = {
  isModalVisible: boolean
  handleToggleModal: () => void
  residents: PartialResident[]
  preferredPeopleNominative?: PeopleNominatives
  carePlansEnabled: boolean
  createResident: (data: ResidentCreateBody) => Promise<any>
  onSuccess?: (resident: any) => void
  testId?: string
}

// Disable future dates and dates before 1900 for birthdate
const disableFutureDates = (current: dayjs.Dayjs) => {
  return current && (current > dayjs().endOf('day') || current.year() < 1900)
}

export const AddResidentModal2 = ({
  isModalVisible,
  handleToggleModal,
  residents,
  carePlansEnabled,
  preferredPeopleNominative,
  createResident,
  onSuccess,
  testId
}: AddResidentModal2Props) => {
  const { t } = useTranslation(NAMESPACE.ADD_RESIDENT_MODAL)
  const [form] = Form.useForm()
  const [isStatusSelectionRequired, setStatusSelectionRequired] = useState(true)
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const dummies = useMemo(
    () => residents.filter((r) => r.status === ResidentStatus.dummy),
    [residents]
  )
  const isInitialAssessmentStatusAvailable = carePlansEnabled
  const isDummyStatusAvailable = dummies.length <= 1

  const statusOptions = [
    {
      label: t(c.STATUS_ACTIVE),
      value: ResidentStatus.active,
      description: t(c.STATUS_ACTIVE_DESCRIPTION)
    }
  ]

  if (isInitialAssessmentStatusAvailable) {
    statusOptions.push({
      label: t(c.STATUS_INITIAL_ASSESSMENT),
      value: ResidentStatus.preAssessment,
      description: t(c.STATUS_INITIAL_ASSESSMENT_DESCRIPTION)
    })
  }

  if (isDummyStatusAvailable) {
    statusOptions.push({
      label: t(c.STATUS_DUMMY_PROFILE),
      value: ResidentStatus.dummy,
      description: t(c.STATUS_DUMMY_PROFILE_DESCRIPTION)
    })
  }

  const status = form.getFieldValue('status')
  const isDummyStatusSelected = status === ResidentStatus.dummy
  const firstStatusValue = statusOptions[0].value
  useEffect(() => {
    /**
     * Initialise the form state for the first step of the wizard
     * appropriately.
     *
     * If only one option available, select it and move forward.
     * If multiple options available, select active by default.
     */
    if (!status && isModalVisible) {
      if (statusOptions.length === 1) {
        form.setFieldsValue({ status: firstStatusValue })
        setStatusSelectionRequired(false)
      } else {
        form.setFieldsValue({ status: ResidentStatus.active })
      }
    }
  }, [statusOptions.length, firstStatusValue, form, status, isModalVisible])

  useEffect(() => {
    /**
     * Auto-populate dummy data when dummy status is selected
     */
    if (isDummyStatusSelected && !isStatusSelectionRequired) {
      const hasOneDummy = dummies.length === 1
      const existingDummy = dummies[0]
      const dummyResident =
        hasOneDummy && existingDummy?.gender === 'male'
          ? DUMMY_DATA.female
          : DUMMY_DATA.male

      const currentValues = form.getFieldsValue()

      form.setFieldsValue({
        name: {
          first: currentValues.name?.first || dummyResident.firstName,
          last: currentValues.name?.last || dummyResident.lastName
        },
        gender: currentValues.gender || dummyResident.gender,
        dateOfBirth: currentValues.dateOfBirth
          ? dayjs(currentValues.dateOfBirth, DATE_FORMAT)
          : dayjs(dummyResident.dateOfBirth, DATE_FORMAT),
        dols: currentValues.dols ?? dummyResident.dols,
        dnacpr: currentValues.dnacpr ?? dummyResident.dnacpr,
        fundingSource:
          currentValues.fundingSource || dummyResident.fundingSource
      })
    }
  }, [isDummyStatusSelected, isStatusSelectionRequired, dummies, form])

  const onConfirm = useCallback(async () => {
    if (isStatusSelectionRequired) {
      // Move to next step
      setStatusSelectionRequired(false)
    } else {
      // Validate and submit form
      try {
        await form.validateFields()
        form.submit()
      } catch (error) {
        // Validation failed, errors will be shown in form
        console.warn('Validation failed:', error)
      }
    }
  }, [isStatusSelectionRequired, form])

  const onCancel = useCallback(() => {
    // Reset form and state
    form.resetFields()
    setStatusSelectionRequired(true)
    setShowDuplicateWarning(false)
    setIsLoading(false)
    handleToggleModal()
  }, [form, handleToggleModal])

  const onFinish = useCallback(
    async (values: FormValues) => {
      setIsLoading(true)
      try {
        const residentData = {
          status: values.status,
          name: {
            first: values.name.first,
            last: values.name.last
          },
          preferredName: values.preferredName,
          gender: values.gender,
          dateOfBirth: values.dateOfBirth?.format(DATE_FORMAT),
          dols: values.dols,
          dnacpr: values.dnacpr,
          fundingSource: values.fundingSource
        } as ResidentCreateBody

        const createdResident = await createResident(residentData)

        // Reset form and close modal
        form.resetFields()
        setStatusSelectionRequired(true)
        setShowDuplicateWarning(false)
        handleToggleModal()

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(createdResident)
        }
      } catch (error) {
        console.error('Failed to create resident:', error)
        // Keep modal open on error
      } finally {
        setIsLoading(false)
      }
    },
    [createResident, form, onSuccess, handleToggleModal]
  )

  const onNameChange = useCallback(() => {
    const firstName = form.getFieldValue(['name', 'first'])
    const lastName = form.getFieldValue(['name', 'last'])

    setShowDuplicateWarning(
      firstName &&
        lastName &&
        residents.some(
          (r) => r.name.first === firstName && r.name.last === lastName
        )
    )
  }, [form, residents])

  const testIdPrefix = testId ? `${testId}-` : ''

  return (
    <Modal
      testId={testId}
      localisation={{
        title: (
          <Title as="h3">{`${t(c.ADD_A_NEW)} ${residentNominative}`}</Title>
        ),
        confirmText: isStatusSelectionRequired ? t(c.NEXT) : t(c.ADD)
      }}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isModalVisible={isModalVisible}
      closeButton
      closeOnConfirm={false}
      confirmButtonProps={{
        isLoading: isLoading,
        variant: 'primary',
        icon: isStatusSelectionRequired ? undefined : (
          <Icon name="PlusOutlined" />
        )
      }}
      data-testid={`${testIdPrefix}add-resident-modal`}
    >
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        data-testid={`${testIdPrefix}add-resident-form`}
      >
        <RequiredFieldsDescription>
          {t(c.REQUIRED_FIELDS_MARKED_WITH)} <Asterisk>*</Asterisk>
          <StyledDivider />
        </RequiredFieldsDescription>
        {isStatusSelectionRequired ? (
          <div>
            <Form.Item
              name="status"
              rules={[{ required: true, message: t(c.PLEASE_SELECT_A_STATUS) }]}
            >
              <Radio.Group
                options={statusOptions.map((option) => ({
                  label: (
                    <RadioOptionLabel>
                      <RadioLabel>{option.label}</RadioLabel>
                      <RadioDescription>{option.description}</RadioDescription>
                    </RadioOptionLabel>
                  ),
                  value: option.value
                }))}
              />
            </Form.Item>
            {isInitialAssessmentStatusAvailable && (
              <HelpText>
                {t(c.NEED_HELP)}{' '}
                <a
                  href="https://support.logmycare.co.uk/en/articles/3376538-how-do-i-use-initial-assessments"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(c.LEARN_ABOUT_INITIAL_ASSESSMENTS)}
                </a>
              </HelpText>
            )}
          </div>
        ) : (
          <>
            <Row gutter={8} align={'bottom'}>
              <Col span={12}>
                <Form.Item
                  label={<Label>{t(c.NAME)}</Label>}
                  name={['name', 'first']}
                  rules={[
                    { required: true, message: t(c.FIRST_NAME_IS_REQUIRED) }
                  ]}
                >
                  <Input
                    placeholder={t(c.FIRST_NAME)}
                    disabled={isDummyStatusSelected}
                    onChange={onNameChange}
                    data-testid={`${testIdPrefix}first-name-input`}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['name', 'last']}
                  rules={[
                    { required: true, message: t(c.LAST_NAME_IS_REQUIRED) }
                  ]}
                  required={false}
                >
                  <Input
                    placeholder={t(c.LAST_NAME)}
                    disabled={isDummyStatusSelected}
                    onChange={onNameChange}
                    data-testid={`${testIdPrefix}last-name-input`}
                  />
                </Form.Item>
              </Col>
            </Row>
            {showDuplicateWarning && (
              <FormWarningWrapper>
                <FormWarning message={t(c.DUPLICATE_NAME_WARNING)} />
              </FormWarningWrapper>
            )}
            <MarginVertical24 />
            <Form.Item
              name="preferredName"
              label={<Label>{t(c.PREFERRED_NAME_OPTIONAL)}</Label>}
            >
              <Input
                disabled={isDummyStatusSelected}
                data-testid={`${testIdPrefix}preferred-name-input`}
              />
            </Form.Item>
            <MarginVertical24 />
            <Form.Item
              name="gender"
              label={<Label>{t(c.GENDER)}</Label>}
              rules={[{ required: true, message: t(c.GENDER_IS_REQUIRED) }]}
            >
              <RadioGroupStyled
                disabled={isDummyStatusSelected}
                options={[
                  { label: t(c.MALE), value: 'male' },
                  { label: t(c.FEMALE), value: 'female' }
                ]}
                data-testid={`${testIdPrefix}gender-radio`}
              />
            </Form.Item>
            <MarginVertical24 />
            <Form.Item
              name="dateOfBirth"
              label={<Label>{t(c.DATE_OF_BIRTH)}</Label>}
              rules={[
                { required: true, message: t(c.DATE_OF_BIRTH_IS_REQUIRED) }
              ]}
            >
              <DatePicker
                format={DATE_FORMAT}
                placeholder={DATE_FORMAT}
                disabled={isDummyStatusSelected}
                disabledDate={disableFutureDates}
                data-testid={`${testIdPrefix}date-of-birth-input`}
              />
            </Form.Item>
          </>
        )}
        <StyledDivider />
      </Form>
    </Modal>
  )
}

const FormWarningWrapper = styled.div`
  margin-top: 8px;
`

const RadioOptionLabel = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0;
`

const RadioLabel = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`

const RadioDescription = styled.div`
  font-size: 12px;
  color: #666;
  line-height: 1.4;
`

const HelpText = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: #666;
  text-align: center;

  a {
    color: #1890ff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const Title = styled(Text)`
  font-weight: 500;
  font-size: 24px;
  text-transform: none;
`

const RequiredFieldsDescription = styled.div`
  bottom: 20px;
  color: ${({ theme }) => `${theme.color.neutral.color4}`};
  font-size: 13px;
  position: relative;
`

const Asterisk = styled.span`
  color: ${({ theme }) => `${theme.palette.red}`};
  font-size: 14px;
  font-weight: bold;
`

const StyledDivider = styled(Divider)`
  margin: 24px 0 0 0;
`
const MarginVertical24 = styled.div`
  margin-bottom: 24px;
`

const RadioGroupStyled = styled(Radio.Group)`
  .ant-radio-wrapper {
    width: auto;
    margin: 0;
  }

  display: flex;
  flex-direction: row;
`

const Label = styled(Text)`
  font-weight: 500;
  font-size: 16px;
`
export default AddResidentModal2
